import React, { useEffect, useState } from "react";

import Image from "next/image";
import { useRouter } from "next/router";

import useTranslation from "next-translate/useTranslation";

import StaticIntlTranslator from "shared/components/atoms/StaticIntlTranslator";
import CheckStar from "shared/components/organisms/Collections/CheckStar";
import ModalCollection from "shared/components/organisms/Collections/RenderModal";
import getFallbackImage from "shared/utils/getFallbackImage";
import { useFeature } from "shared/utils/hooks/useFeature";
import { useGlobalContext } from "shared/utils/hooks/useGlobalContext";
import staticIntlTranslatorToString from "shared/utils/staticIntlTranslatorToString";

import Flags from "./components/Flags";
import TitleContainer from "./components/TitleContainer";
import { ConfigurableCardContainer, ProgressBar } from "./styles";

interface IConfigurableCardProps {
  content: any;
  context: string;
  cardType: string;
  itemRoute: string;
  contextClassName: string;
  nextLessonInfo: any;
}
const ConfigurableCard = ({
  content,
  context = "",
  cardType = "image_only",
  itemRoute = "",
  nextLessonInfo,
  contextClassName = null,
}: IConfigurableCardProps) => {
  const router = useRouter();
  const { lang } = useTranslation();
  const { state } = useGlobalContext();
  const mockEmpty = getFallbackImage();

  const tenantData = state.tenancy;

  const isCollectionsEnabled = useFeature("f_collections");

  const [dragging, setDragging] = useState(false);
  const [failedLoadMainImg, setFailedLoadMainImg] = useState(false);

  useEffect(() => {
    const setDragStateToFalse = () => setDragging(false);
    const setDragStateToTrue = () => setDragging(true);

    document.addEventListener("mousedown", setDragStateToFalse);
    document.addEventListener("mousemove", setDragStateToTrue);

    return () => {
      document.removeEventListener("mousedown", setDragStateToFalse);
      document.removeEventListener("mousemove", setDragStateToTrue);
    };
  }, []);

  const handleClick = (e) => {
    const tagIsClickable =
      e.target.tagName !== "svg" &&
      e.target.tagName !== "path" &&
      e.target.tagName !== "INPUT" &&
      e.target.tagName !== "TEXTAREA" &&
      e.target.className !== "createCollection" &&
      !e.target.className.includes("ant-modal") &&
      !e.target.className.includes("ant-spin") &&
      !e.target.className.includes("ant-empty") &&
      !e.target.className.includes("ant-list") &&
      !e.target.className.includes("ant-checkbox") &&
      e.target.tagName !== "BUTTON";

    if (!dragging && tagIsClickable) {
      router.push(nextLessonInfo?.slug || itemRoute + content?.slug);
    } else {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const contentImg =
    content?.image_thumbnail ||
    content?.thumbnail ||
    content?.thumbnail_image ||
    content?.logo_horizontal;

  const starOnDetailedCardFooter =
    contextClassName && (context === "course" || context === "media");
  const starOnDetailedCardHeader =
    contextClassName && !(context === "course" || context === "media");

  return (
    <ConfigurableCardContainer
      cardType={cardType}
      onClick={handleClick}
      contextClassName={contextClassName}
      starOnDetailedCardFooter={starOnDetailedCardFooter}
      starOnDetailedCardHeader={starOnDetailedCardHeader}
      nextLessonInfo={nextLessonInfo}
    >
      <div className="wrapper">
        {cardType === "image_with_title" && (
          <>
            <TitleContainer
              contextClassName=""
              StaticIntlTranslator={staticIntlTranslatorToString(
                content?.title || content?.name,
                lang,
              )}
            />
            {contextClassName && isCollectionsEnabled && (
              <ModalCollection
                render={({ handleVisible }) => (
                  <CheckStar
                    type={context}
                    entityId={content?.id}
                    className={contextClassName}
                    handleVisible={handleVisible}
                  />
                )}
              />
            )}

            <div className="overlay" />
          </>
        )}

        <div className="image-container-configurable-card">
          <Image
            fill
            alt="banner-item"
            className="lazy-image"
            data-src={contentImg}
            onError={() => setFailedLoadMainImg(true)}
            src={failedLoadMainImg ? mockEmpty : contentImg || mockEmpty}
          />
        </div>

        <Flags flags={content?.flags} isStatic />

        {cardType.includes("complete") && (
          <div className="detailed-card">
            <div className="img-aspect-ratio-container">
              <div className="img-position-container">
                <div className="image-container-configurable-card">
                  <Image
                    layout="fill"
                    alt="banner-item"
                    className="lazy-image"
                    data-src={contentImg}
                    src={
                      failedLoadMainImg ? mockEmpty : contentImg || mockEmpty
                    }
                  />
                </div>

                <Flags flags={content?.flags} />
              </div>
            </div>

            {context === "course" && (
              <ProgressBar
                status="active"
                showInfo={false}
                percent={content?.complete_percentage}
                strokeColor={{
                  "0%": tenantData?.settings["advanced-theme"]
                    ?.themeCourseProgressColor1,
                  "100%":
                    tenantData?.settings["advanced-theme"]
                      ?.themeCourseProgressColor2,
                }}
              />
            )}

            <div className="detailed-card-body">
              <TitleContainer
                contextClassName=""
                StaticIntlTranslator={staticIntlTranslatorToString(
                  content?.title || content?.name,
                  lang,
                )}
              />

              {starOnDetailedCardHeader && (
                <ModalCollection
                  render={({ handleVisible }) => (
                    <CheckStar
                      type={context}
                      entityId={content?.id}
                      className={contextClassName}
                      handleVisible={handleVisible}
                    />
                  )}
                />
              )}

              {(content?.subtitle?.length > 0 ||
                content?.description?.length > 0) && (
                <p className="description">
                  {staticIntlTranslatorToString(
                    content.subtitle || content.description,
                    lang,
                  ).replace(/<.*?>/g, "")}
                </p>
              )}

              {nextLessonInfo && (
                <>
                  <div className="next-lesson-info">
                    <div className="info">
                      <strong>
                        <StaticIntlTranslator id="common:module" />
                      </strong>
                      <p>{nextLessonInfo?.module}</p>
                    </div>
                    <div className="info">
                      <strong>
                        <StaticIntlTranslator id="common:nextLesson" />
                      </strong>
                      <p>{nextLessonInfo?.title}</p>
                    </div>
                  </div>
                </>
              )}

              {cardType !== "complete_without_price" && (
                <div className="detailed-card-footer">
                  <div className="detailed-card-footer-content">
                    {content?.access_type === "free" ? (
                      <span className="info">
                        <StaticIntlTranslator id="common:accessAvailable" />
                      </span>
                    ) : (
                      !state.isMobileAppClient && (
                        <>
                          {content?.lowest_price === "R$ 0,00" ? null : (
                            <>
                              {content?.lowest_price !== content?.price &&
                                content?.status === "published" && (
                                  <span className="info starting-at">
                                    <StaticIntlTranslator id="common:signBy" />
                                  </span>
                                )}

                              <span className="price">
                                {content?.lowest_price}
                              </span>
                            </>
                          )}
                        </>
                      )
                    )}
                  </div>

                  {starOnDetailedCardFooter && (
                    <ModalCollection
                      render={({ handleVisible }) => (
                        <CheckStar
                          type={context}
                          entityId={content?.id}
                          className={contextClassName}
                          handleVisible={handleVisible}
                        />
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </ConfigurableCardContainer>
  );
};

export default React.memo(ConfigurableCard);
